import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../utils/AuthContext';
import logo from '../assets/logo-horizontal.png';
import ProductSwitcher from './ProductSwitcher';
import { orgCanAccessFederation, orgCanAccessPostMatch } from '../utils/productWorkspace';
import {
  LayoutDashboard, Upload, Users, User, Settings as SettingsIcon, FileText, TrendingUp,
  Layers, Search,
  Menu, X, LogOut
} from 'lucide-react';

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout, org } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const role = String(user?.role || '').toLowerCase();
  const isViewer = role === 'viewer';
  const hasPostMatch = orgCanAccessPostMatch(org);
  const inFederation = location.pathname.startsWith('/federation') || !hasPostMatch;
  const showProductSwitcher = orgCanAccessFederation(org) && hasPostMatch;

  const postMatchLinks = [
    { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ...(!isViewer ? [{ to: '/upload', label: 'Analyze Match', icon: Upload }] : []),
    { to: '/matches', label: 'Matches', icon: FileText },
    { to: '/reports', label: 'Reports', icon: TrendingUp },
    { to: '/teams', label: 'Teams', icon: Users },
    { to: '/players', label: 'Players', icon: User },
    { to: '/patterns', label: 'Patterns', icon: Layers },
    { to: '/memory', label: 'Match Memory', icon: Search },
    { to: '/settings', label: 'Settings', icon: SettingsIcon },
  ];

  // Federation workspace uses FederationNav for section links; keep header minimal.
  const links = inFederation
    ? [
        { to: '/federation/dashboard', label: 'Federation', icon: LayoutDashboard },
        { to: '/settings', label: 'Settings', icon: SettingsIcon },
      ]
    : postMatchLinks;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const homeTo = inFederation ? '/federation/dashboard' : '/dashboard';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-navy-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          <Link to={homeTo} className="flex items-center">
            <img src={logo} alt="Tactmark" className="h-11" />
          </Link>

          <div className="hidden lg:flex items-center gap-1">
            {links.map(({ to, label }) => (
              <Link
                key={to}
                to={to}
                aria-current={location.pathname === to ? 'page' : undefined}
                className={`px-3 py-2 rounded text-sm font-medium text-white transition-opacity ${
                  location.pathname === to
                    ? 'bg-navy-700 opacity-100'
                    : 'opacity-80 hover:opacity-100 hover:bg-navy-800'
                }`}
              >
                {label}
              </Link>
            ))}
          </div>

          <div className="hidden lg:flex items-center gap-3">
            {showProductSwitcher && <ProductSwitcher />}
            <span className="text-white opacity-80 text-sm">{user?.email}</span>
            <button
              type="button"
              onClick={handleLogout}
              className="flex items-center gap-1 text-white opacity-80 hover:opacity-100 text-sm transition-opacity focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm"
            >
              <LogOut className="w-4 h-4" />
              Logout
            </button>
          </div>

          <button
            type="button"
            className="lg:hidden text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 rounded-sm"
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-navy-900 border-t border-navy-700 px-4 py-3 space-y-1">
          {showProductSwitcher && (
            <div className="px-3 py-2">
              <ProductSwitcher />
            </div>
          )}
          {links.map(({ to, label, icon: Icon }) => (
            <Link
              key={to}
              to={to}
              aria-current={location.pathname === to ? 'page' : undefined}
              onClick={() => setMobileOpen(false)}
              className={`flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-white transition-opacity ${
                location.pathname === to
                  ? 'bg-navy-700 opacity-100'
                  : 'opacity-80 hover:opacity-100 hover:bg-navy-800'
              }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </Link>
          ))}
          <button
            type="button"
            onClick={handleLogout}
            className="flex items-center gap-2 px-3 py-2 rounded text-sm font-medium text-white opacity-80 hover:opacity-100 w-full text-left"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>
      )}
    </nav>
  );
}
